import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { isRetryable } from '../operator/operatorErrors';
import { offScreenFailure, offScreenSignal } from '../operator/operatorTaskScope';
import {
  addItem,
  addItemProduct,
  addItemSupply,
  printPackingLabels,
  removeItem,
  removeItemSupply,
  requestRelabel,
  saveItem,
  scanPackingCode,
  sendToCourier,
} from './operatorPackingThunks';
import {
  courierSendMessage,
  labelsPrintFailedMessage,
  labelsPrintMessage,
  packingAlreadyRemovedMessage,
  packingMessage,
  packingReloadedMessage,
  relabelRequestMessage,
} from './operatorPackingUtils';

/** What did not happen, said of a write whose order is off screen. */
const FAILED = { saveItem: 'no se guardó', removeItem: 'no se quitó', removeSupply: 'no se quitó' };

/**
 * A write's band once it answered. Off its order's screen (`meta.offScreenTask`)
 * it names the order, says no step to take and offers no «Reintentar»: a
 * packing write only goes again from its order's screen, where the write
 * left unconfirmed waits. On screen, a 412 or 404 is said once the order was
 * reloaded; anything else, the default, with «Reintentar» when sending it
 * again helps.
 */
const write = (kind) => ({
  success: ({ payload, meta }) => {
    const name = meta.offScreenTask;
    const signal = packingMessage(kind, payload, meta.arg, { onScreen: !name });
    return name ? offScreenSignal(name, signal) : signal;
  },
  error: ({ payload, meta }) => {
    const name = meta.offScreenTask;
    if (payload?.alreadyRemoved) {
      const signal = packingAlreadyRemovedMessage(kind, payload);
      return name ? offScreenSignal(name, signal) : signal;
    }
    if (name) return offScreenFailure(name, payload, FAILED[kind]);
    if (typeof payload?.reloaded === 'boolean') return packingReloadedMessage(payload);
    return undefined;
  },
  retry: OPERATOR_FLOW.PACKING,
});

/**
 * What the band says for each packing outcome; see `operatorListeners`. A
 * lost answer offers "Reintentar", which sends the same write again.
 */
export const PACKING_SIGNAL_RULES = {
  [scanPackingCode.typePrefix]: {
    // The write the scan triggered speaks for itself.
    success: ({ payload }) =>
      payload.kind === 'choose' ? { tone: 'warning', message: 'Ese código es de más de un producto: elige cuál.' } : null,
  },
  [addItem.typePrefix]: write('addItem'),
  [saveItem.typePrefix]: write('saveItem'),
  [addItemProduct.typePrefix]: write('addProduct'),
  [removeItem.typePrefix]: write('removeItem'),
  [addItemSupply.typePrefix]: write('addSupply'),
  [removeItemSupply.typePrefix]: write('removeSupply'),
  // Keyed: a lost answer offers «Reintentar» wherever it shows, and its key keeps it from going twice.
  [sendToCourier.typePrefix]: {
    // Its message names the order already.
    success: ({ payload }) => courierSendMessage(payload),
    error: ({ payload, meta }, fallback) => {
      const name = meta.offScreenTask;
      if (!name) return undefined;
      return isRetryable(payload) ? offScreenSignal(name, fallback) : offScreenFailure(name, payload, 'no se envió');
    },
    retry: OPERATOR_FLOW.PACKING,
    keyed: true,
  },
  // As a send: keyed, and its message names the order.
  [requestRelabel.typePrefix]: {
    success: ({ payload }) => relabelRequestMessage(payload),
    error: ({ payload, meta }, fallback) => {
      const name = meta.offScreenTask;
      if (!name) return undefined;
      return isRetryable(payload) ? offScreenSignal(name, fallback) : offScreenFailure(name, payload, 'no se volvió a crear');
    },
    retry: OPERATOR_FLOW.PACKING,
    keyed: true,
  },
  // Only to the visit that asked, and without «Reintentar»: the print button stays on screen.
  [printPackingLabels.typePrefix]: {
    success: ({ payload, meta }) => (meta.offScreenTask || meta.earlierVisit ? null : labelsPrintMessage(payload)),
    error: ({ payload, meta }) => (meta.offScreenTask || meta.earlierVisit ? null : labelsPrintFailedMessage(payload)),
  },
};
